import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { ProcessRunCancelAction } from '@/components/shared/ProcessRunCancelAction';
import { useContentAsyncTaskWatcher, useActiveProcessRunsWatcher } from '@/hooks/content';
import { AsyncTask, useAsyncStore } from '@/store/asyncStore';
import { formatTimeAgo } from '@/utils/timeUtils';
import {
  Ban,
  Bug,
  CheckCircle,
  Clock,
  Loader2,
  RefreshCw,
  Workflow,
  XCircle
} from 'lucide-react';
import { useState } from 'react';

function getStatusIcon(status: AsyncTask['status']) {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-warning" />;
    case 'processing':
      return <Loader2 className="h-4 w-4 text-info animate-spin" />;
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-success" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-destructive" />;
    case 'cancelled':
      return <Ban className="h-4 w-4 text-muted-foreground" />;
    default:
      return <Clock className="h-4 w-4 text-muted-foreground" />;
  }
}

function getStatusBadge(status: AsyncTask['status']) {
  const variants = {
    pending: 'secondary',
    processing: 'default', 
    completed: 'secondary',
    failed: 'destructive',
    cancelled: 'outline'
  } as const;
  
  const labels = {
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed', 
    failed: 'Failed',
    cancelled: 'Cancelled'
  };

  return (
    <Badge variant={variants[status]} className="text-xs">
      {labels[status]}
    </Badge>
  );
}


function AsyncTaskItem({ task }: { task: AsyncTask }) {
  const { updateTask } = useAsyncStore();
  
  const debugCompleteTask = () => {
    console.log(`[AsyncQueue] Manually completing task ${task.id}`);
    updateTask(task.id, { 
      status: 'completed',
      progress: 100,
      description: 'Manually completed'
    });
  };

  return (
    <div data-testid="async-queue-row" className="flex items-start gap-3 p-3 hover:bg-muted border-b border-border last:border-b-0">
      <div className="flex-shrink-0 mt-1">
        {getStatusIcon(task.status)}
      </div>
      
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-medium text-foreground truncate">
            {task.title}
          </h4>
          <div className="flex items-center gap-1">
            <span data-testid="async-queue-row-status">{getStatusBadge(task.status)}</span>
            {task.status === 'processing' && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-5 px-1"
                onClick={debugCompleteTask}
                title="Complete task manually"
              >
                <Bug className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
        
        {task.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">
            {task.description}
          </p>
        )}
        
        {task.status === 'processing' && task.progress !== undefined && (
          <div className="space-y-1">
            <Progress value={task.progress} className="h-1.5" />
            <p className="text-xs text-muted-foreground">{task.progress}% complete</p>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {formatTimeAgo(task.createdAt)}
          </span>

          {task.contentType === 'PROCESS_RUN' && task.contentId !== undefined && task.runStatus !== undefined && (
            <ProcessRunCancelAction runId={task.contentId} kind="provenance" status={task.runStatus} />
          )}

          {task.status === 'failed' && (
            <Button variant="ghost" size="sm" className="h-6 px-2">
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AsyncQueue() {
  const [isOpen, setIsOpen] = useState(false);
  const { getActiveTasks, updateTask } = useAsyncStore();

  useContentAsyncTaskWatcher(getActiveTasks(), updateTask);
  useActiveProcessRunsWatcher();
  
  const activeTasks = getActiveTasks();
  const hasActiveTasks = activeTasks.length > 0;
  const hasProcessingTasks = activeTasks.some((t) => t.status === 'processing');

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          data-testid="async-queue-trigger"
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 relative"
          title={hasActiveTasks ? `${activeTasks.length} active background tasks` : 'No background tasks'}
        >
          {hasProcessingTasks ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Workflow className="h-4 w-4 text-muted-foreground" />
          )}
          {hasActiveTasks && (
            <Badge 
              data-testid="async-queue-count"
              variant="secondary" 
              className="absolute top-0 right-0 h-4 w-4 p-0 text-[10px] bg-info text-info-foreground border-0 rounded-full flex items-center justify-center"
            >
              {activeTasks.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        data-testid="async-queue-panel"
        className="w-80 p-0 shadow-lg border"
        align="end"
        side="bottom"
        sideOffset={5}
      >
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-medium text-sm">Background tasks</h3>
          <span className="text-xs text-muted-foreground">
            {activeTasks.length} active
          </span>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {activeTasks.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <Workflow className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm">No background tasks</p>
            </div>
          ) : (
            <div>
              {activeTasks.map((task) => (
                <AsyncTaskItem key={task.id} task={task} />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
